export type PaymentMethod = 'cash' | 'pin' | 'loan' | 'worker_pickup';
export type PaymentStatus = 'paid' | 'unpaid' | 'internal';
export type TransactionType = 'pickup' | 'reservation' | 'reservation_fulfillment';

export interface Customer {
  id: string;
  admin_id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  vat_number?: string;
  notes?: string;
  total_purchases: number;
  purchase_count: number;
  last_purchase_at?: string;
  is_favorite: boolean;
  created_at: string;
  updated_at: string;
}

export interface HeldCart {
  id: string;
  admin_id: string;
  created_by: string;
  customer_id?: string;
  customer_name?: string;
  cart_items: HeldCartItem[];
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  notes?: string;
  expires_at: string;
  created_at: string;
  updated_at: string;
}

export interface HeldCartItem {
  id: string;
  name: string;
  category?: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  color_id?: string;
  size_id?: string;
  image_url?: string;
}

export interface FavoriteProduct {
  id: string;
  admin_id: string;
  user_id: string;
  product_id: string;
  sort_order: number;
  created_at: string;
}

export interface POSTransaction {
  id: string;
  admin_id: string;
  transaction_number: string;
  invoice_id?: string;
  reservation_id?: string;
  customer_id?: string;
  customer_name: string;
  payment_method: PaymentMethod;
  payment_status: PaymentStatus;
  worker_id?: string;
  worker_name?: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  items_count: number;
  notes?: string;
  created_by: string;
  created_at: string;
}

export interface POSDashboardStats {
  todaySalesCount: number;
  todayRevenue: number;
  pendingReservations: number;
  expiringReservations: number;
  heldCartsCount: number;
  cashTotal: number;
  pinTotal: number;
  loanTotal: number;
  workerPickupTotal: number;
}

export interface POSQuickAction {
  id: string;
  label: string;
  icon: string;
  shortcut?: string;
  action: () => void;
}

export interface WorkerInfo {
  id: string;
  name: string;
  employee_id?: string;
}

export interface PaymentMethodConfig {
  method: PaymentMethod;
  label: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
  paymentStatus: PaymentStatus;
  requiresWorkerInfo: boolean;
}

export const PAYMENT_METHOD_CONFIGS: Record<PaymentMethod, PaymentMethodConfig> = {
  cash: {
    method: 'cash',
    label: 'Cash',
    description: 'Immediate cash payment',
    icon: 'Banknote',
    color: 'text-green-700 dark:text-green-300',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-500',
    paymentStatus: 'paid',
    requiresWorkerInfo: false
  },
  pin: {
    method: 'pin',
    label: 'PIN',
    description: 'Card/PIN terminal payment',
    icon: 'CreditCard',
    color: 'text-blue-700 dark:text-blue-300',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-500',
    paymentStatus: 'paid',
    requiresWorkerInfo: false
  },
  loan: {
    method: 'loan',
    label: 'Loan',
    description: 'Invoice on credit (unpaid)',
    icon: 'FileText',
    color: 'text-orange-700 dark:text-orange-300',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    borderColor: 'border-orange-500',
    paymentStatus: 'unpaid',
    requiresWorkerInfo: false
  },
  worker_pickup: {
    method: 'worker_pickup',
    label: 'Worker Pick Up',
    description: 'Internal staff pickup',
    icon: 'UserCheck',
    color: 'text-gray-700 dark:text-gray-300',
    bgColor: 'bg-gray-50 dark:bg-gray-800',
    borderColor: 'border-gray-500',
    paymentStatus: 'internal',
    requiresWorkerInfo: true
  }
};
